import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Landmark, TrendingUp, TrendingDown, AlertCircle,
  ChevronDown, ChevronRight, RefreshCw, Loader2, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import SPMappingDialog from './SPMappingDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (v) => {
  if (v == null || isNaN(v)) return '-';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0
  }).format(v);
};

const fmtPct = (v) => {
  if (v == null || isNaN(v) || !isFinite(v)) return '';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
};

const BalanceSheetProgressive = ({ clientId, periods }) => {
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [reprocessing, setReprocessing] = useState(false);
  const [showSPMapping, setShowSPMapping] = useState(false);
  const [spMappingData, setSpMappingData] = useState(null);

  const availableYears = useMemo(() => {
    if (!periods?.length) return [];
    return [...new Set(periods.map(p => p.year))].sort((a, b) => b - a);
  }, [periods]);

  useEffect(() => {
    if (availableYears.length && !year) {
      setYear(String(availableYears[0]));
    }
  }, [availableYears, year]);

  const fetchData = useCallback(async () => {
    if (!year || !clientId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    try {
      const resp = await fetch(`${API}/api/clients/${clientId}/balance-sheet-progressive?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        setData(await resp.json());
      } else {
        toast.error('Errore nel caricamento');
      }
    } catch {
      toast.error('Errore di connessione');
    }
    setLoading(false);
  }, [year, clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReprocess = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setReprocessing(true);
    try {
      const resp = await fetch(`${API}/api/clients/${clientId}/reprocess-sp?force=true`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const result = await resp.json();
        if (result.updated > 0) {
          toast.success(`Estratti dati SP da ${result.updated} periodi`);
        } else if (result.skipped > 0 && (!result.sp_mapping_info || result.sp_mapping_info.unmapped?.length === 0)) {
          toast.info('Dati SP presenti e tutti i conti mappati');
        }
        if (result.sp_mapping_info && result.sp_mapping_info.unmapped?.length > 0) {
          toast.info(`${result.sp_mapping_info.unmapped.length} conti SP da configurare`);
          setSpMappingData({
            unmapped: result.sp_mapping_info.unmapped,
            chartType: result.sp_mapping_info.chart_type,
          });
          setShowSPMapping(true);
        }
        fetchData();
      }
    } catch {
      toast.error('Errore di connessione');
    }
    setReprocessing(false);
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const activeMonths = useMemo(() => {
    return (data?.months || []).filter(m => m.has_sp_data);
  }, [data]);

  const hasData = activeMonths.length > 0;
  const rowDefs = data?.row_definitions || [];

  // Row styling
  const getRowStyle = (style) => {
    switch (style) {
      case 'section_header':
        return 'bg-slate-800 text-white font-bold text-xs uppercase tracking-wide';
      case 'detail':
        return 'bg-white text-slate-700 text-sm hover:bg-slate-50';
      case 'subtotal':
        return 'bg-slate-100 font-semibold text-slate-800 border-t border-slate-300';
      case 'total':
        return 'bg-indigo-900 text-white font-bold border-t-2 border-indigo-700';
      default:
        return 'bg-white text-slate-700';
    }
  };

  const getCellColor = (style, value) => {
    if (style === 'section_header' || style === 'total') return '';
    if (value == null || isNaN(value) || value === 0) return 'text-slate-300';
    return value < 0 ? 'text-red-600' : 'text-slate-800';
  };

  if (!data && loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="balance-sheet-progressive">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Landmark className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Stato Patrimoniale Comparato</CardTitle>
                <span className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                  Confronto progressivo mese per mese
                  {hasData && (
                    <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                      {activeMonths.length} mesi con dati
                    </Badge>
                  )}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReprocess}
                disabled={reprocessing}
                data-testid="reprocess-sp-btn"
              >
                {reprocessing
                  ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Riprocessamento...</>
                  : <><RefreshCw className="h-4 w-4 mr-1" /> Estrai dati SP</>
                }
              </Button>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-28" data-testid="sp-year-select">
                  <SelectValue placeholder="Anno" />
                </SelectTrigger>
                <SelectContent>
                  {(availableYears || []).map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!hasData ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <AlertCircle className="h-12 w-12 text-amber-400 mb-4" />
              <h3 className="text-base font-semibold text-slate-700 mb-2">Nessun dato SP disponibile</h3>
              <p className="text-sm text-muted-foreground max-w-md mb-4">
                Clicca il pulsante per estrarre le voci patrimoniali dai bilanci caricati.
              </p>
              <Button onClick={handleReprocess} disabled={reprocessing} data-testid="reprocess-sp-empty-btn">
                {reprocessing
                  ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Riprocessamento...</>
                  : <><RefreshCw className="h-4 w-4 mr-2" /> Estrai dati SP dai file caricati</>
                }
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              {activeMonths.length === 1 && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Solo 1 mese con dati patrimoniali ({activeMonths[0].month_name})</p>
                    <p className="text-amber-700 mt-0.5">
                      Per vedere il confronto progressivo, i bilanci mensili caricati devono contenere anche le voci patrimoniali
                      (codici 01-46 per piano S1, 100-299 per GENERIC). Verifica che i file del tuo gestionale includano lo Stato Patrimoniale.
                    </p>
                  </div>
                </div>
              )}
              <table className="w-full border-collapse text-sm" data-testid="sp-table">
                <thead>
                  <tr className="bg-slate-800">
                    <th className="text-left py-2.5 px-3 text-white font-semibold text-xs uppercase tracking-wide sticky left-0 bg-slate-800 z-10 min-w-[220px] border-r border-slate-700">
                      Voce
                    </th>
                    {activeMonths.map((m, idx) => (
                      <th key={m.month} className="text-right py-2.5 px-3 text-white font-semibold text-xs min-w-[105px] border-r border-slate-700">
                        <div>{m.month_name}</div>
                        <div className="text-[10px] font-normal text-slate-400">{year}</div>
                      </th>
                    ))}
                    {activeMonths.length >= 2 && (
                      <th className="text-right py-2.5 px-3 text-amber-300 font-semibold text-xs min-w-[120px] bg-slate-900">
                        <div className="flex items-center justify-end gap-1">
                          <ArrowRight className="h-3 w-3" /> Var. Periodo
                        </div>
                        <div className="text-[10px] font-normal text-slate-400">
                          {activeMonths[0].month_name} - {activeMonths[activeMonths.length-1].month_name}
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rowDefs.map((rd, idx) => {
                    if (rd.style === 'section_header') {
                      const sectionKey = rd.key;
                      const isExpanded = expandedSections[sectionKey] !== false;
                      return (
                        <tr key={idx} className={getRowStyle(rd.style) + ' cursor-pointer'}
                            onClick={() => toggleSection(sectionKey)}
                            data-testid={`sp-section-${rd.key}`}>
                          <td className="py-2 px-3 sticky left-0 bg-slate-800 z-10 border-r border-slate-700"
                              colSpan={activeMonths.length + (activeMonths.length >= 2 ? 2 : 1)}>
                            <span className="flex items-center gap-2">
                              {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                              {rd.label}
                            </span>
                          </td>
                        </tr>
                      );
                    }

                    const parentSection = rowDefs.slice(0, idx).reverse().find(r => r.style === 'section_header');
                    if (parentSection && expandedSections[parentSection.key] === false && rd.style === 'detail') {
                      return null;
                    }

                    const firstMonth = activeMonths[0];
                    const lastMonth = activeMonths[activeMonths.length - 1];
                    const firstVal = firstMonth?.values?.[rd.key] || 0;
                    const lastVal = lastMonth?.values?.[rd.key] || 0;
                    const variation = lastVal - firstVal;
                    const variationPct = firstVal !== 0 ? ((lastVal - firstVal) / Math.abs(firstVal)) * 100 : null;

                    return (
                      <tr key={idx} className={getRowStyle(rd.style)} data-testid={`sp-row-${rd.key}`}>
                        <td className={`py-1.5 px-3 sticky left-0 z-10 border-r border-slate-200 ${
                          rd.style === 'total' ? 'bg-indigo-900' :
                          rd.style === 'subtotal' ? 'bg-slate-100' : 'bg-white'
                        }`}>
                          {rd.label}
                        </td>

                        {activeMonths.map((m, mIdx) => {
                          const val = m.values?.[rd.key];
                          const prevMonth = mIdx > 0 ? activeMonths[mIdx - 1] : null;
                          const prevVal = prevMonth?.values?.[rd.key] || 0;
                          const delta = prevMonth ? (val || 0) - prevVal : 0;
                          const showTrend = prevMonth && delta !== 0 && rd.style === 'detail';

                          return (
                            <td key={m.month} className={`text-right py-1.5 px-3 border-r border-slate-100 ${getCellColor(rd.style, val)}`}>
                              <div>
                                {val != null && val !== 0 ? fmtEur(val) : <span className="text-slate-300">-</span>}
                              </div>
                              {showTrend && (
                                <div className={`text-[10px] ${delta > 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                                  {delta > 0 ? '+' : ''}{fmtEur(delta)}
                                </div>
                              )}
                            </td>
                          );
                        })}

                        {activeMonths.length >= 2 && (
                          <td className={`text-right py-1.5 px-3 font-medium ${
                            rd.style === 'total' ? 'bg-indigo-950 text-white' :
                            rd.style === 'subtotal' ? 'bg-slate-200 text-slate-800' :
                            variation > 0 ? 'bg-emerald-50 text-emerald-700' :
                            variation < 0 ? 'bg-red-50 text-red-600' :
                            'text-slate-400 bg-slate-50'
                          }`}>
                            {variation !== 0 ? (
                              <div>
                                <div className="flex items-center justify-end gap-1">
                                  {variation > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                                  {fmtEur(variation)}
                                </div>
                                {variationPct != null && isFinite(variationPct) && (
                                  <div className="text-[10px] opacity-70">{fmtPct(variationPct)}</div>
                                )}
                              </div>
                            ) : <span className="text-slate-300">-</span>}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Summary cards for key totals */}
              {activeMonths.length >= 2 && (() => {
                const first = activeMonths[0];
                const last = activeMonths[activeMonths.length - 1];
                const attivo0 = first.values?.totale_attivo || 0;
                const attivo1 = last.values?.totale_attivo || 0;
                const passivo0 = first.values?.totale_passivo || 0;
                const passivo1 = last.values?.totale_passivo || 0;
                const pn0 = first.values?.total_mezzi_propri || 0;
                const pn1 = last.values?.total_mezzi_propri || 0;

                return (
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                    <SummaryCard
                      label="Totale Attivo"
                      from={attivo0} to={attivo1}
                      fromLabel={first.month_name} toLabel={last.month_name}
                    />
                    <SummaryCard
                      label="Totale Passivo"
                      from={passivo0} to={passivo1}
                      fromLabel={first.month_name} toLabel={last.month_name}
                    />
                    <SummaryCard
                      label="Patrimonio Netto"
                      from={pn0} to={pn1}
                      fromLabel={first.month_name} toLabel={last.month_name}
                      highlight
                    />
                  </div>
                );
              })()}

              {/* Legend */}
              <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-emerald-600" /> Aumento
                </span>
                <span className="flex items-center gap-1">
                  <TrendingDown className="h-3 w-3 text-red-500" /> Diminuzione
                </span>
                <span>Sotto ogni importo: variazione rispetto al mese precedente</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <SPMappingDialog
        open={showSPMapping}
        onClose={() => setShowSPMapping(false)}
        unmappedAccounts={spMappingData?.unmapped || []}
        chartType={spMappingData?.chartType || 'GENERIC'}
        onSaved={() => {
          setShowSPMapping(false);
          toast.success('Mappature SP salvate!');
          fetchData();
        }}
      />
    </div>
  );
};

const SummaryCard = ({ label, from, to, fromLabel, toLabel, highlight }) => {
  const delta = to - from;
  const pct = from !== 0 ? ((to - from) / Math.abs(from)) * 100 : null;

  return (
    <div className={`rounded-lg p-3 ${
      highlight ? 'bg-indigo-50 border-2 border-indigo-200' : 'bg-slate-50 border border-slate-200'
    }`} data-testid={`sp-summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">{label}</p>
      <div className="flex items-center justify-between gap-2">
        <div className="text-center">
          <p className="text-[10px] text-slate-400">{fromLabel}</p>
          <p className="text-sm font-semibold text-slate-700">{fmtEur(from)}</p>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
        <div className="text-center">
          <p className="text-[10px] text-slate-400">{toLabel}</p>
          <p className={`text-sm font-bold ${highlight ? 'text-indigo-700' : 'text-slate-800'}`}>{fmtEur(to)}</p>
        </div>
      </div>
      {delta !== 0 && (
        <div className={`text-center mt-2 text-xs font-medium ${delta > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {delta > 0 ? '+' : ''}{fmtEur(delta)}
          {pct != null && isFinite(pct) && <span className="ml-1 opacity-70">({fmtPct(pct)})</span>}
        </div>
      )}
    </div>
  );
};

export default BalanceSheetProgressive;
